<!-- @format -->
<template>
    <div class="marketing-plan-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Marketing Plan Settings</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="loading-container" v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div class="marketing-plan-name-container">
                        <app-input v-model="marketingPlan.name" label="Marketing Plan Name" />
                    </div>
                    <div class="marketing-plan-category-container">
                        <global-category-dropdown
                            :account-id="accountId"
                            show-selected-up
                            :selected="marketingPlan.category"
                            @input="addListCategories($event)"
                            @remove="deleteCategory"
                            placeholder="Select a category"
                        />
                    </div>
                    <div class="marketing-plan-description-container">
                        <app-text-area
                            v-model="marketingPlan.description"
                            label="Marketing Plan Description"
                        />
                    </div>
                    <div class="marketing-plan-color-container">
                        <div class="marketing-plan-color-label">Marketing Plan Color</div>
                        <p>
                            This color is meant to help you easily identify your marketing plans at
                            a glance.
                        </p>
                        <app-marketing-plan-color-select
                            v-model="marketingPlan.color"
                            :right-aligned="true"
                        />
                    </div>

                    <div class="marketing-plan-email-container">
                        <div class="marketing-plan-email-label">
                            Select who the campaigns from this plan will be sent out from
                        </div>
                        <div v-show="hasErrors" class="error-message">
                            Please ensure From name and From email fields are filled.
                        </div>
                        <div class="email-form-row">
                            <div class="name-input-container">
                                <app-input v-model="marketingPlan.fromName" label="Name" />
                            </div>
                            <div class="email-input-container">
                                <app-input
                                    v-model="marketingPlan.fromEmail"
                                    label="youremail@email.com"
                                    :is-valid="$v.marketingPlan.fromEmail.email"
                                    error="Please enter a valid email."
                                />
                            </div>
                        </div>
                        <domain-authentication-check
                            :client-id="clientId"
                            :from-email="marketingPlan.fromEmail"
                        />
                    </div>
                    <div class="marketing-plan-list-container">
                        <div class="marketing-plan-list-label">Choose your list</div>
                        <div class="list-container">
                            <list-search
                                :client-id="clientId"
                                @input="addList($event)"
                                placeholder="Select list"
                            />
                            <div class="lists">
                                <div
                                    v-for="list in marketingPlan.lists"
                                    :key="list.id"
                                    class="list"
                                    :class="list.class"
                                >
                                    {{ list.name }}
                                    <s3-close-svg @click.native="removeList(list)" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button class="save-btn" @click="saveMarketingPlan()">
                        <span v-if="marketingPlan.id">Save</span>
                        <span v-else>Next Step</span>
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const DomainAuthenticationCheck = () => import('@/components/email/domain-authentication-check');
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const AppTextArea = () => import('@/components/common/app-textarea');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const AppMarketingPlanColorSelect = () =>
    import('@/components/common/app-marketing-plan-color-select');
const ListSearch = () => import('@/components/list/list-search');
const GlobalCategoryDropdown = () => import('@/components/common/global-category-dropdown');
const S3CloseSvg = () => import('@/components/svg/grid/s3-close-svg');
import { required, email } from 'vuelidate/lib/validators';

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'marketing-plan-widget',

    components: {
        'app-button': AppButton,
        'app-text-area': AppTextArea,
        'app-input': AppInput,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'app-marketing-plan-color-select': AppMarketingPlanColorSelect,
        'list-search': ListSearch,
        'global-category-dropdown': GlobalCategoryDropdown,
        's3-close-svg': S3CloseSvg,
        'domain-authentication-check': DomainAuthenticationCheck,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,
            clientId: this.widget.clientId,
            marketingPlanTemplateId: this.widget.marketingPlanTemplateId,
            marketingPlan: {
                id: this.widget.marketingPlanId,
                name: null,
                category: [],
                description: null,
                color: '#F30070',
                fromName: null,
                fromEmail: null,
                lists: [],
            },

            hasErrors: false,
        };
    },

    validations() {
        return {
            marketingPlan: {
                fromEmail: {
                    required,
                    email,
                },

                fromName: {
                    required,
                },
            },
        };
    },

    computed: {
        accountId() {
            return this.widget.accountId;
        },

        hasTemplateCategories() {
            return this.widget.hasTemplateCategories;
        },
    },

    created() {
        if (this.marketingPlan.id) {
            this.getMarketingPlan().then(this.hydrateLists);
        } else if (this.marketingPlanTemplateId) {
            this.getMarketingPlanTemplate();
        } else {
            this.isLoading = false;
        }
    },

    methods: {
        addListCategories(item) {
            const exist = this.marketingPlan.category.some((element) => element === item.name);

            if (!exist) {
                this.marketingPlan.category.push(item.name);
            }
        },

        deleteCategory(item) {
            this.marketingPlan.category.splice(this.marketingPlan.category.indexOf(item), 1);
        },

        addList(newList) {
            var exists = this.marketingPlan.lists.some((list) => list.id === newList.id);

            if (!exists) {
                this.marketingPlan.lists.push(newList);
            }
        },

        removeList(list) {
            this.marketingPlan.lists.splice(this.marketingPlan.lists.indexOf(list), 1);
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        saveMarketingPlan() {
            if (this.marketingPlan.id) {
                this.patchMarketingPlan();
            } else {
                this.postMarketingPlan().then(() => {
                    var route = `/MarketingPlans/${this.marketingPlan.id}/Calendar`;

                    if (this.marketingPlanTemplateId) {
                        route += `?marketingPlanTemplateId=${this.marketingPlanTemplateId}`;
                    }

                    this.$router.push(route);
                });
            }
        },

        postMarketingPlan() {
            return new Promise((resolve, reject) => {
                this.$v.$touch();
                if (this.$v.$invalid) {
                    this.hasErrors = true;
                    reject();
                    return;
                }

                this.isLoading = true;

                var request = {
                    ClientId: this.clientId,
                    MasterAgencyId: this.masterAgencyId,
                    AgencyId: this.agencyId,
                    Name: this.marketingPlan.name,
                    Description: this.marketingPlan.description,
                    FromName: this.marketingPlan.fromName,
                    FromEmail: this.marketingPlan.fromEmail,
                    Color: this.marketingPlan.color,
                    ListIds: this.marketingPlan.lists.map((list) => list.id),
                    Categories: this.marketingPlan.category,
                };

                this.$api
                    .post('/api/MarketingPlans', request)
                    .then((response) => {
                        this.marketingPlan.id = response.data.Id;
                        this.$helpers.showSuccessNotification('Marketing Plan saved successfully.');
                        this.closeWidget();
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Error creating marketing plan.', {
                            error,
                        });
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            });
        },

        patchMarketingPlan() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                this.hasErrors = true;
                return;
            }

            this.isLoading = true;

            var changes = [
                {
                    name: 'Name',
                    value: this.marketingPlan.name,
                },
                {
                    name: 'Description',
                    value: this.marketingPlan.description,
                },
                {
                    name: 'FromName',
                    value: this.marketingPlan.fromName,
                },
                {
                    name: 'FromEmail',
                    value: this.marketingPlan.fromEmail,
                },
                {
                    name: 'Color',
                    value: this.marketingPlan.color,
                },
                {
                    name: 'ListIds',
                    value: this.marketingPlan.lists.map((list) => list.id),
                },
                {
                    name: 'Categories',
                    value: this.marketingPlan.category,
                },
            ];

            this.$api
                .patch(`/api/MarketingPlans/${this.marketingPlan.id}`, changes)
                .then(() => {
                    this.$helpers.showSuccessNotification('Marketing Plan saved successfully.');
                    EventBus.$emit('marketing-plan:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Error updating marketing plan.', { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        getMarketingPlan() {
            return new Promise((resolve, reject) => {
                this.isLoading = true;
                const selectParam = '$select=Name,Description,FromName,FromEmail,Color';
                const filterParam = `$filter=Id eq ${this.marketingPlan.id}`;
                const expandParam = '$expand=MarketingPlanLists,Categories';
                const restUrl = `/service/MarketingPlans?${expandParam}&${filterParam}&${selectParam}`;

                this.$api
                    .get(restUrl)
                    .then((response) => {
                        var marketingPlanResponse = response.data.value[0];
                        this.marketingPlan.name = marketingPlanResponse.Name;
                        this.marketingPlan.description = marketingPlanResponse.Description;
                        this.marketingPlan.fromName = marketingPlanResponse.FromName;
                        this.marketingPlan.fromEmail = marketingPlanResponse.FromEmail;
                        this.marketingPlan.color = marketingPlanResponse.Color || '#F30070';
                        this.marketingPlan.category = marketingPlanResponse.Categories.map(
                            ({ Name }) => Name,
                        );

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while getting your marketing plan.',
                            { error },
                        );
                        reject();
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            });
        },

        getMarketingPlanTemplate() {
            return new Promise((resolve, reject) => {
                this.isLoading = true;
                const selectParam = '$select=Name, Description';
                const filterParam = `$filter=Id eq ${this.marketingPlanTemplateId}`;
                const queryParams = [filterParam, selectParam];

                if (this.hasTemplateCategories) {
                    queryParams.push('$expand=Categories');
                }

                const restUrl = `/service/MarketingPlanTemplates?${queryParams.join('&')}`;

                this.$api
                    .get(restUrl)
                    .then((response) => {
                        var marketingPlanTemplateResponse = response.data.value[0];
                        this.marketingPlan.name = marketingPlanTemplateResponse.Name;
                        this.marketingPlan.description = marketingPlanTemplateResponse.Description;
                        this.marketingPlan.color = '#F30070';
                        this.marketingPlan.category = marketingPlanTemplateResponse.Categories
                            ? marketingPlanTemplateResponse.Categories.map(({ Name }) => Name)
                            : [];

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while getting your marketing plan template.',
                            { error },
                        );
                        reject();
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            });
        },

        hydrateLists() {
            var select = '$select=Name,Id';
            this.$api
                .get(
                    `/service/Lists?$expand=ListContacts($count=true;$top=0)&$filter=MarketingPlanLists/any(l: l/MarketingPlanId eq ${this.marketingPlan.id})&${select}`,
                )
                .then((response) => {
                    this.marketingPlan.lists = response.data.value.map((list) => {
                        return {
                            name: list.Name,
                            id: list.Id,
                            count: list['ListContacts@odata.count'],
                        };
                    });
                });
        },
    },
};
</script>

<style>
.marketing-plan-widget .loading-screen,
.marketing-plan-widget .loading-container {
    text-align: center;
    height: 100%;
}

.marketing-plan-widget .marketing-plan-email-container .marketing-plan-email-label,
.marketing-plan-widget .marketing-plan-color-container .marketing-plan-color-label,
.marketing-plan-widget .marketing-plan-list-container .marketing-plan-list-label {
    font-weight: 500;
}

.marketing-plan-widget .marketing-plan-color-container .marketing-plan-color-label {
    margin-bottom: 0px;
}

.marketing-plan-widget .trigger-select-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
}

.marketing-plan-widget .list-trigger-container .add-btn {
    width: 100%;
    height: 39px;
    background-color: #18aeff;
    border-radius: 22px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    margin-top: 15px;
}

.marketing-plan-widget .list-input-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    padding: 22px 56px;
}

.marketing-plan-widget .lists .list {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #877d88;
    border-radius: 20px;
    font-size: 16px;
    color: #ffffff;
    padding: 11px 15px;
    margin-top: 11px;
}

.marketing-plan-widget .lists .list .delete-list {
    cursor: pointer;
}

.marketing-plan-widget .lists .list .s3-close-svg .a {
    stroke: #fff;
}

.marketing-plan-widget .list-trigger-con.add-btn .plus-sign {
    height: 21px;
    width: 21px;
}

.marketing-plan-widget .list-trigger-container .add-btn .plus-sign .default {
    fill: #ffffff;
}

.marketing-plan-widget .cancel-btn {
    /* background: transparent;
    color: #877d88;
    border: none; */
    background: transparent;
    border: 1px solid #cac6cb;
    color: #382153;
}

.marketing-plan-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.marketing-plan-widget .marketing-plan-name-container,
.marketing-plan-widget .marketing-plan-description-container,
.marketing-plan-widget .marketing-plan-color-container,
.marketing-plan-widget .marketing-plan-category-container {
    margin-bottom: 23px;
}

.marketing-plan-widget .trigger-select-container {
    margin-bottom: 30px;
}

.marketing-plan-widget .trigger-label {
    position: relative;
    bottom: -12px;
    width: fit-content;
    background: #ffffff;
    margin: 0 auto;
    font-weight: 500;
    padding: 0 58px;
}

.marketing-plan-widget .list-label,
.marketing-plan-widget .date-label {
    position: relative;
    bottom: -12px;
    width: fit-content;
    background: #ffffff;
    margin: 0 auto;
    font-weight: 500;
    padding: 0 47px;
}

.marketing-plan-widget .list-trigger-container {
    margin-bottom: 30px;
}

.marketing-plan-widget .date-trigger-container .date-and-time-container {
    padding: 22px 56px;
    border: 2px solid #e9e9ef;
    border-radius: 22px;
}

.marketing-plan-widget .date-trigger-container .date-and-time-container .hour,
.marketing-plan-widget .date-trigger-container .date-and-time-container .minute {
    width: 20px;
}

.marketing-plan-widget .date-trigger-container .date-and-time-container .time-multiselect,
.marketing-plan-widget .date-trigger-container .date-and-time-container .time-zone-multiselect {
    width: 20px;
}

.marketing-plan-widget .date-trigger-container .date-and-time-container .time-container {
    display: flex;
    justify-content: space-evenly;
}

.marketing-plan-widget .timezone {
    width: 300px;
}

.marketing-plan-widget .app-color-picker {
    position: absolute;
    top: 35px;
    z-index: 100;
}

.marketing-plan-widget .marketing-plan-color-container p {
    color: #958d97;
    font-size: 16px;
}

.marketing-plan-widget .email-form-row {
    display: flex;
    justify-content: space-between;
}

.marketing-plan-widget .input-container {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -ms-flex-direction: column;
    flex-direction: column;
    width: 100%;
}

.marketing-plan-widget .name-input-container {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -ms-flex-direction: column;
    flex-direction: column;
    width: 320px;
}

.marketing-plan-widget .email-input-container {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -ms-flex-direction: column;
    flex-direction: column;
    width: 320px;
    margin-left: 10px;
}

.marketing-plan-widget .email-form-row label {
    display: none;
}

.marketing-plan-widget .marketing-plan-name-container label,
.marketing-plan-widget .marketing-plan-description-container label,
.marketing-plan-widget .marketing-plan-email-label,
.marketing-plan-widget .marketing-plan-color-label,
.marketing-plan-widget .marketing-plan-name-label {
    margin-bottom: 13px;
}

.marketing-plan-widget .list-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    padding: 22px 32px;
}

.marketing-plan-widget .marketing-plan-list-container {
    margin-top: 24px;
    text-align: center;
}

.marketing-plan-widget .marketing-plan-list-label {
    background: #fff;
    position: relative;
    bottom: -10px;
    display: inline-block;
    padding: 0 40px;
}

.marketing-plan-widget .list {
    color: #fff;
    padding: 10px;
    font-size: 16px;
    border-radius: 20px;
    position: relative;
    margin-top: 12px;
}

.marketing-plan-widget .list svg {
    width: 14px;
    cursor: pointer;
    position: absolute;
    right: 14px;
    top: 50%;
    margin-top: -10px;
}

.marketing-plan-widget .lists .list svg path {
    fill: #fff;
}

.marketing-plan-widget .error-message {
    color: #f30070;
    font-size: 12px;
}

.marketing-plan-widget .app-textarea textarea {
    height: 158px;
    resize: none;
}

.marketing-plan-widget .app-input input {
    border: 1px solid #cac6cb;
    padding: 8px 16px;
    height: 40px;
}

.marketing-plan-widget .app-input input:hover {
    border-color: #877d88;
}

.marketing-plan-widget .app-input input:hover,
.marketing-plan-widget .app-input input:focus {
    background-color: #fff;
    border: solid #18aeff;
    border-width: 2px;
    padding: 7px 15px;
}

.marketing-plan-widget .widget-contents-container .loading-screen {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    margin-left: 0;
}
</style>
